/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from "react";
import {
  IconDownload,
  IconEye,
  IconPlus,
  IconSend,
  IconSearch,
  IconX, 
} from "@tabler/icons-react";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import DropdownFilter from "../../../components/common/DropdownFilter"; 
import ToggleButton from "../../../components/ToggleButton";
import AddCompanyModal from "./AddCompanyModal";
import RequestSendModal from "./RequestSendModal";
import { useForm } from "react-hook-form";
import {
  useFetchLineHaulData,
  useLineHaulCreateMutation,
  useLineHaulUpdateMutation,
  useSendRequestMutation,
  useUpdateLineHaulStatus,
} from "../../../apis/fleetApis";
import MessageModal from "../../../components/MessageModal";
import formatDate from "../../../utilities/helper"; 
import toast from "react-hot-toast";
import Pagination from "../../../components/common/Pagination";
import NoDataFound from "../../../components/common/NoDataFound";
import useDebounce from "../../../utilities/useDebounce";
import EntriesSelector from "../../../components/common/EntriesSelector";
import { handleDownload } from "../../../utilities/downloadHelper";
import VehicleCardSkeleton from "../../../components/skeleton/VehicleCardSkeleton";
import { usePermission } from "../../../hooks/usePermissionTabs";
import { T, useLanguage } from "../../../context/LanguageContext";

type LineData = {
  id: string;
};

type FormValues = {
  companyName: string;
  contactPerson: string;
  email: string;
  mobileNumber: string;
  address: string;
};

type FormValues1 = {
  fromEmail: string;
  originWarehouseId: string;
  destinationWarehouseId: string;
  linehaulCompanyId: { id: string; value?: string; label?: string }[];
  weightCapacity: string;
  onDate: string;
  cutOffTime: string;
};

type Props = {
  activeRole: string;
  setHeaderActions?: (node: React.ReactNode) => void;
  setHeaderButtons?: (node: React.ReactNode) => void;
};

// Centralized modal control
type ModalState =
  | { type: "none" }
  | { type: "company"; mode: "add" | "edit" | "view"; item?: any }
  | { type: "request"; singleCompany: boolean; item?: any }
  | { type: "status"; item: any };

const LineHaulCompanies = ({
  activeRole,
  setHeaderButtons,
  setHeaderActions,
}: Props) => {
  const { t } = useLanguage();
  const { allowed: canCreateLineHaul } = usePermission("line_haul", "create");
  const { allowed: canUpdateLineHaul } = usePermission("line_haul", "update");

  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<string>("");
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const debouncedSearchFor = useDebounce(searchFor, 550);
  const [modal, setModal] = useState<ModalState>({ type: "none" });
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [allSelectedData, setAllSelectedData] = useState<LineData[]>([]);
  const [showCheckboxes, setShowCheckboxes] = useState<boolean>(false);
  const isSidePanelOpen = modal.type === "company" || modal.type === "request";
  const { data, refetch, isLoading, isFetching } = useFetchLineHaulData(
    pageNo,
    limit,
    debouncedSearchFor,
  );
  const { mutate: UpdateLineHaulStatus, isPending: activePending } =
    useUpdateLineHaulStatus();
  const { mutate: createMutate, isPending: createPending } =
    useLineHaulCreateMutation();
  const { mutate: updateMutation, isPending: updatePending } =
    useLineHaulUpdateMutation(
      modal.type === "company" && modal.item ? modal.item.id : undefined,
    );
  const { mutate: sendCompanyRequest, isPending: sendPending } =
    useSendRequestMutation();

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  useEffect(() => {
    if (data?.results) {
      setAllSelectedData((prev) => {
        const filteredPrev = prev.filter((item: any) =>
          selectedRows.includes(item.id),
        );
        const newSelected = data.results.filter(
          (s: any) =>
            selectedRows.includes(s.id) &&
            !filteredPrev.some((item) => item.id === s.id),
        );
        return [...filteredPrev, ...newSelected];
      });
    }
  }, [data?.results, selectedRows]);

  useEffect(() => {
    refetch();
  }, [debouncedSearchFor, pageNo, limit]);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    clearErrors,
    control,
    formState: { errors },
  } = useForm<FormValues>();

  const {
    register: assignRegister,
    handleSubmit: handleRequestSubmit,
    clearErrors: clearAssignErrors,
    setValue: setAssignValue,
    watch: assignWatch,
    reset: assignReset,
    control: assignControl,
    formState: { errors: assignErrors },
  } = useForm<FormValues1>();

  const handleOpenCompany = useCallback(
    (mode: "add" | "edit" | "view", item?: any) => {
      setModal({ type: "company", mode, item });

      if (item) {
        setValue("companyName", item.companyName);
        setValue("contactPerson", item.contactPerson);
        setValue("mobileNumber", item.mobileNumber);
        setValue("email", item.email);
        setValue("address", item.address);
      } else {
        reset();
      }
    },
    [setValue, reset],
  );

  const handleSaveLineHaul = (formData: FormValues) => {
    if (modal.type === "company" && modal.mode === "edit" && modal.item) {
      updateMutation(formData, {
        onSuccess: (res: any) => {
          setModal({ type: "none" });
          reset();
          refetch();
          toast.success(t(res?.message || "Company updated successfully!"));
        },
        onError: (err: any) => {
          console.error("Update failed:", err);
          toast.error(
            t(
              err?.response?.data?.message ||
                err?.message ||
                "Failed to update company"
            )
          );
        },
      });
    } else {
      createMutate(formData, {
        onSuccess: (res: any) => {
          setModal({ type: "none" });
          reset();
          refetch();
          toast.success(t(res?.message || "Company added successfully!"));
        },
        onError: (err: any) => {
          console.error("Create failed:", err);
          toast.error(
            t(
              err?.response?.data?.message ||
                err?.message ||
                "Failed to create company"
            )
          );
        },
      });
    }
  };

  const handleSendRequest = (data: any) => {
    sendCompanyRequest(
      {
        ...data,
        linehaulCompanyId: data?.linehaulCompanyId?.map(
          (linehaul: any) => linehaul?.id,
        ),
      },
      {
        onSuccess: () => {
          setModal({ type: "none" });
          assignReset();
          refetch();
        },
        onError: (error) => {
          console.log(error, "error");
          toast.error(t(String(error)));
        },
      },
    );
  };

  const handleUpdateStatus = async () => {
    if (modal.type !== "status" || !modal.item) return;
    const newStatus = modal.item.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    await UpdateLineHaulStatus({ id: modal.item.id, status: newStatus });
    setModal({ type: "none" });
    refetch();
  };

  useEffect(() => {
    refetch();
  }, [activeRole]);

  const [columnsData, setColumnsData] = useState([
    { text: "Checkbox", value: "checkbox", isShow: showCheckboxes },
    { text: "S/N", value: "sn", isShow: true },
    { text: "Company Name", value: "companyName", isShow: true },
    { text: "Contact", value: "contactPerson", isShow: true },
    { text: "Mobile", value: "mobile", isShow: true },
    { text: "Email", value: "email", isShow: true },
    { text: "Address", value: "address", isShow: true },
    { text: "Date", value: "date", isShow: true },
    { text: "Status", value: "status", isShow: true },
    { text: "Actions", value: "actions", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const handleSelectRow = (id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === data?.results?.length) {
      setSelectedRows((prev) =>
        prev.filter((id) => !data.results.some((c: any) => c.id === id)),
      );
    } else {
      const newSelections =
        data?.results
          ?.filter((c: any) => !selectedRows.includes(c.id))
          .map((c: any) => c.id) || [];
      setSelectedRows((prev) => [...prev, ...newSelections]);
    }
  };

  const columns = columnsData
    .filter((c) => c.isShow)
    .map((col) => {
      switch (col.value) {
        case "checkbox":
          return {
            label: (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={
                    data?.results?.length > 0 &&
                    data?.results?.every((c: any) =>
                      selectedRows.includes(c.id),
                    )
                  }
                  onChange={handleSelectAll}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                 bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                />
                <span
                  className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                  top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                >
                  ✓
                </span>
              </label>
            ),
            render: (c: any) => (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedRows.includes(c.id)}
                  onChange={() => handleSelectRow(c.id)}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                  bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                />
                <span
                  className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                                top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                >
                  ✓
                </span>
              </label>
            ),
          };
        case "sn":
          return {
            label: t("#"),
            render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
          };
        case "companyName":
          return { label: t("Company Name"), render: (c: any) => c.companyName };
        case "contactPerson":
          return { label: t("Contact"), render: (c: any) => c.contactPerson };
        case "mobile":
          return { label: t("Mobile"), render: (c: any) => c.mobileNumber };
        case "email":
          return { label: t("Email"), render: (c: any) => c.email };
        case "address":
          return { label: t("Address"), render: (c: any) => c.address };
        case "date":
          return {
            label: t("Date"),
            render: (c: any) => formatDate(c.created_at),
          };
        case "status":
          return {
            label: t("Status"),
            render: (c: any) => (
              <div className="flex items-center justify-center">
                <ToggleButton
                  onChange={() => {
                    if (!canUpdateLineHaul) {
                      toast.error(
                        "You do not have permission to update company status",
                      );
                      return;
                    }
                    setModal({ type: "status", item: c });
                  }}
                  checked={c.status === "ACTIVE"}
                />
              </div>
            ),
          };

        case "actions":
          return {
            label: t("Actions"),
            render: (c: any) => (
              <div className="flex items-center justify-center gap-1 text-[#212121]">
                <button
                  onClick={() => handleOpenCompany("view", c)}
                  className="p-2 border rounded-full bg-[#F7F6F2] text-[#212121]"
                >
                  <IconEye strokeWidth={1.5} size={22} />
                </button>
                {canCreateLineHaul && (
                  <button
                    onClick={() => {
                      setAssignValue("linehaulCompanyId", [
                        {
                          id: c.id,
                          value: c.companyName,
                          label: c.companyName,
                        },
                      ]);
                      setModal({
                        type: "request",
                        singleCompany: true,
                        item: c,
                      });
                    }}
                    className="p-2 border rounded-full bg-[#F7F6F2] text-[#212121]"
                    title={t("Send Request")}
                  >
                    <IconSend strokeWidth={1.5} size={22} />
                  </button>
                )}
              </div>
            ),
          };
        default:
          return { label: "", render: () => null };
      }
    });

  const mapDataToExport = (c: any, index: number) => ({
    "S/N": index + 1,
    "Company Name": c.companyName,
    Contact: c.contactPerson,
    Mobile: c.mobileNumber,
    Email: c.email,
    Address: c.address,
    Date: formatDate(c.created_at),
    Status: c.status,
  });

  return (
    <div className="grid grid-cols-12 gap-4 relative w-full">
      {/* Backdrop overlay for mobile & tablet screen sizes */}
      {isSidePanelOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => {
            setModal({ type: "none" });
            reset();
            assignReset();
          }}
        />
      )}

      <div
        className={`overflow-visible mb-6 transition-all duration-300 ${
          isSidePanelOpen ? "col-span-12 lg:col-span-8 xl:col-span-7" : "col-span-12"
        }`}
      >
        {/* Header Row (Filters + Actions Unified) */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4">
          {/* Left side: Brand badge */}
          <div className="flex flex-wrap items-center gap-3">
          </div>

          {/* Right side: Export + Search + Advanced Filters + Primary Button */}
          <div className="flex items-center flex-wrap gap-2.5">
            {/* Sliding Search */}
            <div className="relative flex items-center">
              <div
                onClick={() => {
                  if (!showSearch) {
                    setShowSearch(true);
                  }
                }}
                className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                  showSearch
                    ? "w-64 px-2 cursor-default"
                    : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                }`}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSearch(!showSearch);
                  }}
                  className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                  title={t("Search")}
                >
                  <IconSearch size={17} stroke={2.5} />
                </button>
                {showSearch && (
                  <>
                    <input
                      type="text"
                      value={searchFor}
                      onChange={(e) => {
                        setSearchFor(e.target.value);
                        setPageNo(1);
                      }}
                      placeholder={t("Search...")}
                      autoFocus
                      className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchFor("");
                        setShowSearch(false);
                      }}
                      className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                      title={t("Clear & Close")}
                    >
                      <IconX size={14} stroke={2.5} />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Export */}
            <button
              onClick={() =>
                handleDownload({
                  selectedRows,
                  allSelectedData,
                  setSelectedRows,
                  setAllSelectedData,
                  showCheckboxes,
                  setShowCheckboxes,
                  setColumnsData,
                  mapDataToExport,
                  fileName: "linehaul_companies.xlsx",
                })
              }
              className={`w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer ${
                showCheckboxes && selectedRows.length === 0
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              title={t("Export to Excel")}
            >
              <IconDownload size={17} stroke={2.5} />
              {selectedRows.length > 0 && (
                <span className="ml-1 text-xs font-bold">
                  ({selectedRows.length})
                </span>
              )}
            </button>

            {/* Entries limit selector */}
            {data && data.totalPages * limit > 10 && (
              <EntriesSelector
                value={limit}
                options={[
                  10,
                  ...(data.totalPages * limit > 10 ? [20] : []),
                  ...(data.totalPages * limit > 20 ? [30] : []),
                  ...(data.totalPages * limit > 50 ? [50] : []),
                  ...(data.totalPages * limit > 100 ? [100] : []),
                ]}
                onChange={(val) => {
                  setLimit(val);
                  setPageNo(1);
                }}
              />
            )}

            {/* Columns Filter */}
            <ColumnsFilter
              columnsData={columnsData.filter((c) => c.value !== "checkbox")}
              setHeadings={setHeadings}
            />

            {/* Dropdown Filter */}
            <DropdownFilter
              filterheadings={[]}
              triggerClassName="border border-slate-200 bg-white rounded-lg cursor-pointer text-[#212121] hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-150 h-8 w-8 flex items-center justify-center shadow-sm select-none"
              iconSize={17}
            />

            {/* Create New Request */}
            {canCreateLineHaul && (
              <button
                onClick={() =>
                  setModal({ type: "request", singleCompany: false })
                }
                className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer flex-shrink-0"
                title={t("Create New Request")}
              >
                <IconSend size={17} stroke={2.5} />
              </button>
            )}

            {/* Add Company */}
            {canCreateLineHaul && (
              <button
                onClick={() => handleOpenCompany("add")}
                className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-150 border border-[#FBD037] bg-[#FBD037] text-[#131327] hover:bg-[#e2bd30] shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer flex-shrink-0"
                title={t("Add Company")}
              >
                <IconPlus size={17} stroke={2.5} />
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div>
          <div className="">
            {isLoading ? (
              <VehicleCardSkeleton rows={6} />
            ) : data?.results && data?.results.length > 0 ? (
              <div
                className={`grid grid-cols-1 md:grid-cols-2 ${isSidePanelOpen ? "xl:grid-cols-3" : "lg:grid-cols-3"} gap-5`}
              >
                {data?.results.map((v: any, idx: number) => (
                  <div
                    key={v.id || idx}
                    onClick={() => handleSelectRow(v.id)}
                    className={`group cursor-pointer rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border p-4 transition-all duration-300 space-y-3 relative ${
                      selectedRows.includes(v.id)
                        ? "border-[#FBD037] ring-1 ring-[#FBD037]"
                        : "border-[#D2D2D2]/70"
                    }`}
                  >
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-[#D2D2D2]/70 pb-2 relative z-10">
                      <div>
                        {/* <p className="text-[11px] text-[#212121] uppercase tracking-wider">
                          {v.vehicle_number || "V-001"}
                        </p> */}
                        <h3 className="text-[#000000] font-semibold text-lg mt-1 line-clamp-1">
                          {v.companyName}
                        </h3>
                      </div>

                      <span
                        onClick={() => {
                          if (!canUpdateLineHaul) {
                            toast.error(
                              "You do not have permission to update company status",
                            );
                            return;
                          }
                          setModal({ type: "status", item: v });
                        }}
                        className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors hover:opacity-80 relative z-10 ${
                          v.status === "ACTIVE"
                            ? "bg-green-500/15 text-green-500"
                            : "bg-red-500/15 text-red-500"
                        }`}
                      >
                        {v.status === "ACTIVE" ? t("Active") : t("Inactive")}
                      </span>
                    </div>

                    {/* Info Section */}
                    <div className="space-y-3 relative z-10">
                      <div className="flex items-center justify-between">
                        <span className="text-[#212121] text-sm">
                          <T>Contact Person</T>:
                        </span>
                        <span className="text-[#212121] text-sm font-medium">
                          {v.contactPerson || ""}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-[#212121] text-sm">
                          <T>Mobile Number</T>:
                        </span>
                        <span className="text-[#212121] text-sm font-medium">
                          {v.mobileNumber || ""}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-[#212121] text-sm"><T>Email</T>:</span>
                        <span className="text-[#212121] text-sm font-medium">
                          {v.email || ""}
                        </span>
                      </div>
                    </div>

                    {/* Action Button */}
                    <div className="flex items-center gap-2 relative z-10">
                      <button
                        className="flex-1 h-10 rounded-lg bg-[#FBD037]/20 text-[#000000] text-sm font-medium hover:bg-[#FBD037]/30 transition-colors"
                        onClick={() => handleOpenCompany("view", v)}
                      >
                        <T>View Details</T>
                      </button>
                      {canCreateLineHaul && (
                        <button
                          onClick={() => {
                            setAssignValue("linehaulCompanyId", [
                              {
                                id: v.id,
                                value: v.companyName,
                                label: v.companyName,
                              },
                            ]);
                            setModal({
                              type: "request",
                              singleCompany: true,
                              item: v,
                            });
                          }}
                          className="h-10 w-10 flex items-center justify-center rounded-lg bg-[#FBD037]/20 text-[#000000]"
                          title={t("Send Request")}
                        >
                          <IconSend size={20} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <NoDataFound />
            )}
          </div>
        </div>
        <div className="py-4 flex justify-center">
          {data?.totalPages > 1 && (
            <Pagination
              totalPages={data?.totalPages}
              currentPage={pageNo}
              fetchData={handlePaginationFetch}
            />
          )}
        </div>
      </div>

      {modal.type === "company" && (
        <div className="fixed top-20 bottom-0 right-0 z-50 w-full sm:max-w-2xl lg:max-w-none bg-white/20 p-4 sm:p-6 lg:p-0 lg:static lg:col-span-4 xl:col-span-5 lg:top-auto lg:bottom-auto animate-slide-left flex flex-col h-[calc(100vh-80px)] lg:h-full rounded-l-3xl lg:rounded-3xl border border-[#D2D2D2]/50 shadow-sm overflow-hidden bg-white lg:bg-transparent">
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 ">
            <h2 className="text-xl font-semibold text-[#212121]">
              {modal.mode === "edit" || modal.mode === "view"
                ? t("Company Details")
                : t("Add Company")}
            </h2>

            <div className="flex items-center gap-3">
              {modal.mode === "view" ? (
                canUpdateLineHaul && (
                  <button
                    onClick={() =>
                      setModal({
                        type: "company",
                        mode: "edit",
                        item: modal.item,
                      })
                    }
                    className="px-4 py-2 bg-[#FBD037] text-[#212121] rounded-lg disabled:opacity-50"
                  >
                    {t("Edit")}
                  </button>
                )
              ) : (
                <button
                  disabled={updatePending || createPending}
                  onClick={handleSubmit(
                    handleSaveLineHaul,
                    (validationErrors) => {
                      const errors = Object.values(validationErrors);
                      if (errors.length > 0) {
                        const firstErr = errors[0] as any;
                        if (firstErr?.message) toast.error(t(firstErr.message));
                      }
                    },
                  )}
                  className="px-4 py-2 bg-[#FBD037] text-[#212121] rounded-lg disabled:opacity-50"
                >
                  {modal.mode === "edit" ? t("Update") : t("Add")}
                </button>
              )}

              <button
                onClick={() => {
                  setModal({ type: "none" });
                  reset();
                }}
                className="p-2 rounded-lg border border-[#D2D2D2]"
              >
                <IconX size={20} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 scrollbar-clean">
            <AddCompanyModal
              register={register}
              errors={errors}
              setValue={setValue}
              watch={watch}
              clearErrors={clearErrors}
              control={control}
              isEdit={modal.mode === "edit"}
              viewMode={modal.mode === "view"}
            />
          </div>
        </div>
      )}

      {modal.type === "request" && (
        <div className="fixed top-20 bottom-0 right-0 z-50 w-full sm:max-w-2xl lg:max-w-none bg-white/20 p-4 sm:p-6 lg:p-0 lg:static lg:col-span-4 xl:col-span-5 lg:top-auto lg:bottom-auto animate-slide-left flex flex-col h-[calc(100vh-80px)] lg:h-full rounded-l-3xl lg:rounded-3xl border border-[#D2D2D2]/50 shadow-sm overflow-hidden bg-white lg:bg-transparent">
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 bg-white/50">
            <h2 className="text-xl font-semibold text-[#212121]">
              <T>Create New Request</T>
            </h2>

            <div className="flex items-center gap-3">
              <button
                disabled={sendPending}
                onClick={handleRequestSubmit(handleSendRequest)}
                className="px-4 py-2 bg-[#FBD037] text-[#212121] rounded-lg disabled:opacity-50"
              >
                {sendPending ? t("Sending...") : t("Send")}
              </button>

              <button
                onClick={() => {
                  setModal({ type: "none" });
                  assignReset();
                }}
                className="p-2 rounded-lg border border-[#D2D2D2]"
              >
                <IconX size={20} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto pb-8 scrollbar-clean">
            <RequestSendModal
              register={assignRegister}
              errors={assignErrors}
              setValue={setAssignValue as any}
              watch={assignWatch}
              clearErrors={clearAssignErrors as any}
              control={assignControl}
              singleMode={modal.singleCompany}
            />
          </div>
        </div>
      )}

      {modal.type === "status" && (
        <MessageModal
          open={true}
          onClose={() => setModal({ type: "none" })}
          title={t("Confirmation Required")}
          subtitle={t("Are you sure you want to perform this action?")}
          img={
            modal.item?.status === "ACTIVE"
              ? "/img/inactive.svg"
              : "/img/active.svg"
          }
          showFooter={true}
          closeButtonText={t("Cancel")}
          actionButtonText={t("Confirm")}
          actionButtonOnClick={handleUpdateStatus}
          isPending={activePending}
          size="md"
        />
      )}
    </div>
  );
};

export default LineHaulCompanies;
